import {Component} from 'react';
import {Translate} from 'react-redux-i18n';
import {isEmpty} from 'front-onceforall-core/dist/utils/arrays';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {Icon} from 'front-onceforall-core';
import {emailValidator} from 'front-onceforall-core/dist/utils/validators';
import TextField from '@material-ui/core/TextField';
import {connect} from 'react-redux';
import userService from '../../../../services/user/UserService';
import enrollmentService from '../../../../services/enrollment';
import {nestedAccess} from '../../../../utils/objects';
import InviteUser from '../../../userAdd/inviteUser';
import {translateToLocale} from '../../../../translations';
import {INVITATION_TYPE} from '../../../../utils/constant';
import * as snackbarsActions from '../../../../actions/snackbars';
import authenticationService from '../../../../services/authentication/AuthenticationService';
import UserInvitation from '../userInvitation';

const REQUIRED_FIELDS = [
  'email'
];

export class UserAddFormView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      confirmLoading: false,
      inviteLoading: false,
      openExternalInvitation: false,
      fieldErrors: {},
      showFieldErrors: false,
      email: this.props.email,
      invitedUser: null
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleAddUser = this.handleAddUser.bind(this);
    this.translateInvitationMessage = this.translateInvitationMessage.bind(this);
    this.handleCancelExternalInvitation = this.handleCancelExternalInvitation.bind(this);
    this.handleValidateExternalInvitation = this.handleValidateExternalInvitation.bind(this);
    this.inviteUser = this.inviteUser.bind(this);
    this.findUserOrCreateOne = this.findUserOrCreateOne.bind(this);
    this.handleApiCallbacksErrors = this.handleApiCallbacksErrors.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);

    this.isBackOffice = authenticationService.roles().isBackOffice();
  }

  async handleApiCallbacksErrors(error) {
    const errorJson = await error.response.json();
    const fieldErrors = {};
    REQUIRED_FIELDS.forEach(fieldName => {
      if (errorJson.message === 'error.deactivatedUser') {
        fieldErrors[fieldName] = <Translate value="user.invite.deactivatedUser"/>;
      } else if (errorJson.message === 'error.userAlreadyTiedToOrganisation') {
        fieldErrors[fieldName] = <Translate value="user.invite.userAlreadyTiedToOrganisation"/>;
      } else if (errorJson.message === 'error.selfInvitation') {
        fieldErrors[fieldName] = <Translate value="user.invite.self"/>;
      } else {
        fieldErrors[fieldName] = <Translate value="error.generic"/>;
      }
    });
    this.setState({
      fieldErrors,
      showFieldErrors: true
    });
  }

  inviteUser(inviteFrom, customMessage) {
    const user = this.state.invitedUser;
    const invitation = {
      targetUserId: user.id,
      targetUserEmail: user.email,
      targetUserLastName: user.lastName,
      targetUserFirstName: user.firstName,
      targetUserPhoneNumber: user.phoneNumber ? user.phoneNumber : user.mobilePhoneNumber,
      targetUserLanguage: user.language,
      targetOrganisationId: this.props.targetOrganisation.organisationId,
      invitationType: inviteFrom,
      customMessage: customMessage || this.translateInvitationMessage(user.language)
    };

    enrollmentService.general.inviteUser(invitation)
      .then(() => {
        this.props.actions.addSuccess(<Translate
          value="user.invite.successfullyInvitedUserToRelationship"
          name={`${invitation.targetUserFirstName} ${invitation.targetUserLastName}`}
          organisationName={this.props.targetOrganisation.fullName}
        />);
        this.props.onClose();
      })
      .catch(error => this.handleApiCallbacksErrors(error));
  }

  findUserOrCreateOne() {
    this.setState({
      inviteLoading: true
    });
    userService.searchByEmail(this.state.email)
      .then(response => {
        this.setState({inviteLoading: false});
        if (response.status === 204) {
          this.setState({openExternalInvitation: true});
        } else {
          response.json().then(user => {
            this.setState({invitedUser: user});
          });
        }
      });
  }

  handleKeyPress(target) {
    if (target.key === 'Enter') {
      this.handleAddUser();
      target.preventDefault();
    }
  }

  handleAddUser() {
    this.checkIfSubmittable();
    if (!this.state.isSubmittable) {
      this.setState({showFieldErrors: true});
      return;
    }

    if (this.state.inviteLoading) {
      return;
    }

    this.findUserOrCreateOne();
  }

  checkIfSubmittable() {
    this.setState({
      isSubmittable: !this.validateFields()
    });
  }

  validateFields() {
    const fieldErrors = {};
    let hasError = false;
    REQUIRED_FIELDS.forEach(fieldName => {
      const value = nestedAccess(fieldName, this.state);
      if (isEmpty(value) || value === false) {
        fieldErrors[fieldName] = <Translate value="error.required"/>;
        hasError = true;
      } else if (emailValidator(value) === 'error.invalidEmailFormat') {
        fieldErrors[fieldName] = <Translate value="error.validation"/>;
        hasError = true;
      } else {
        fieldErrors[fieldName] = null;
      }
    });
    this.setState({
      fieldErrors
    });
    return hasError;
  }

  handleInputChange(event) {
    const {value} = event.target;
    this.setState({
      email: value
    }, () => this.checkIfSubmittable());
  }

  translateInvitationMessage(language = this.props.currentLocale) {
    return translateToLocale(language, 'user.invite.defaultUserMessage', {
      userName: this.props.user.fullName,
      invitingOrganisationName: this.props.organisation.fullName,
      invitingOrganisationRegistrationNumber: this.props.organisation.registrationNumber,
      invitedOrganisationName: this.props.targetOrganisation.fullName,
      invitedOrganisationRegistrationNumber: this.props.targetOrganisation.registrationNumber
    });
  }

  handleCancelExternalInvitation() {
    this.setState({
      openExternalInvitation: false,
      email: ''
    });
  }

  handleValidateExternalInvitation(invitation) {
    this.setState({invitedUser: {
      id: null,
      email: invitation.email,
      firstName: invitation.firstName,
      lastName: invitation.lastName,
      phoneNumber: invitation.phoneNumber,
      language: invitation.language
    }}, () => this.inviteUser(INVITATION_TYPE.EXTERNAL, invitation.customMessage));

    this.setState({
      openExternalInvitation: false,
      confirmLoading: false
    });

    return Promise.resolve();
  }

  getFieldErrorText(fieldName) {
    if (this.state.fieldErrors === null || typeof (this.state.fieldErrors[fieldName]) === 'undefined' || !this.state.showFieldErrors) {
      return null;
    }

    return this.state.fieldErrors[fieldName];
  }

  render() {
    const searchButton = (
      <button
        data-id="inviteUserButton"
        type="button"
        className={'large inline-container no-wrap primary'}
        onClick={this.handleAddUser}
        disabled={this.state.inviteLoading}
      >
        <Translate value="organisation.users.searchLabel"/>
      </button>
    );

    const userInviteForm = (this.state.invitedUser && !this.state.openExternalInvitation ?
      <UserInvitation
        user={this.state.invitedUser}
        onAddUser={() => this.inviteUser(INVITATION_TYPE.INTERNAL)}
      /> : <div/>
    );

    return (
      <div>
        {this.state.openExternalInvitation ?
          <InviteUser
            actions={this.props.actions}
            onCancel={this.handleCancelExternalInvitation}
            onValidate={this.handleValidateExternalInvitation}
            translateInvitationMessage={this.translateInvitationMessage}
            currentLocale={this.props.currentLocale}
            userMail={this.state.email}
            panelTitle="user.invite.panelTitle"
          /> :
          <div>
            <h2 className="mb-5"><Translate value="user.invite.panelTitle"/></h2>
            <form>
              <div>
                <TextField
                  autoFocus
                  label={<span><Translate value="user.email"/> *</span>}
                  error={Boolean(this.getFieldErrorText('email'))}
                  helperText={this.getFieldErrorText('email')}
                  data-id="email"
                  value={this.state.email}
                  onChange={this.handleInputChange}
                  onKeyDown={this.handleKeyPress}
                  InputProps={{
                    endAdornment: (
                      searchButton
                    )
                  }}
                />
                {userInviteForm}
              </div>
              <div className="actions">
                <button
                  type="button"
                  className="large secondary flex-container"
                  onClick={this.props.onClose}
                >
                  <Icon icon="go-back"/>
                  <Translate value="action.cancel"/>
                </button>
              </div>
            </form>
          </div>
        }
      </div>
    );
  }
}

UserAddFormView.propTypes = {
  actions: PropTypes.object.isRequired,
  organisation: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  currentLocale: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  email: PropTypes.string,
  targetOrganisation: PropTypes.object.isRequired
};

UserAddFormView.defaultProps = {
  email: ''
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation,
    user: state.user,
    currentLocale: state.i18n.locale
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({...snackbarsActions}, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UserAddFormView);
